import React, { useEffect, useMemo, useState } from 'react';
//import Select from 'react-select';
import { CheckIcon, PlusIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { checkApi } from '../../Authentication/AxiosInstance';

const MAX_PREFERRED = 10;

// Centralize API paths so they can be easily adjusted
const API = {
  profile: '/candidates/candidate-details/my_profile/',
  updateProfile: '/candidates/candidate-details/update_profile/',
  branches: '/masters/branches-all/', //For getting available branches (belonging to core)
  core: '/masters/branches-core/', //For getting available core branches
};

const SDPrefBranches = ({ candidateId, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [preferredBranches, setPreferredBranches] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  // New: checkbox-based core branches + Other input
  const CORE_BRANCHES = [
    'COMPUTER SCIENCE',
    'ELECTRICAL',
    'ELECTRONICS',
    'CIVIL',
    'MECHANICAL'
  ];
  const [selectedCoreBranches, setSelectedCoreBranches] = useState([]);
  const [otherBranchesInput, setOtherBranchesInput] = useState('');
  const [otherValidationMsg, setOtherValidationMsg] = useState('');

  const canAddMore = preferredBranches.length < MAX_PREFERRED;

  const loadPreferred = async () => {
    try {
      setLoading(true);
      const res = await checkApi.get(API.profile);
      const branchDetails = res.data.preferred_branch_details || [];
      setPreferredBranches(branchDetails);
    } catch (e) {
      console.log(e)
      setError('Failed to load preferred branches.');
    } finally {
      setLoading(false);
    }
  };

  const loadAllBranches = async () => {
    try {
      const res = await checkApi.get(API.branches);
      const branches = res.data || [];
      setAllBranches(branches);
      
      // Convert to options format
      const options = branches.map(branch => ({ 
        value: branch.id, 
        label: branch.branch_name || branch.name || 'Unknown Branch'
      }));
      
      // Sort alphabetically
      if (options && Array.isArray(options)) {
        options.sort((a, b) => {
        const labelA = a.label || '';
        const labelB = b.label || '';
        return labelA.localeCompare(labelB);
      });
      }
      setBranchOptions(options || []);
      
    } catch (e) {
      console.log(e)
      setError('Failed to load branches data.');
    }
  };

  useEffect(() => {
    loadPreferred();
    loadAllBranches();
  }, []);

  // Helpers for checkbox selection
  const toggleCoreBranch = (name) => {
    setSelectedCoreBranches((prev) => {
      if (prev.includes(name)) return prev.filter(b => b !== name);
      return [...prev, name];
    });
  };

  const parseAndValidateOther = () => {
    setOtherValidationMsg('');
    const text = otherBranchesInput || '';
    const tokens = text.split(',').map(t => t.trim()).filter(Boolean);
    if (tokens.length === 0) return { valid: [], invalid: [] };
    const allNames = new Set(
      (allBranches || []).map(b => (b.branch_name || b.name || '').toLowerCase())
    );
    const valid = [];
    const invalid = [];
    tokens.forEach(tok => {
      if (allNames.has(tok.toLowerCase())) {
        // normalize to canonical case from master where possible
        const match = (allBranches || []).find(
          b => (b.branch_name || b.name || '').toLowerCase() === tok.toLowerCase()
        );
        valid.push(match?.branch_name || match?.name || tok);
      } else {
        invalid.push(tok);
      }
    });
    return { valid, invalid };
  };

  const handleSaveSelected = async () => {
    setError('');
    setSuccess('');
    // candidateId not needed when using my_profile/update_profile

    const { valid, invalid } = parseAndValidateOther();
    if (invalid.length > 0) {
      setOtherValidationMsg(`There is no such branch: ${invalid.join(', ')}`);
      return;
    }

    // Pull existing courses from profile to merge
    try {
      setSaving(true);
      const currentResponse = await checkApi.get(API.profile);
      const currentCourses = currentResponse.data.candidate_preferred_courses || '';
      const existing = currentCourses ? currentCourses.split(',').map(c => c.trim()).filter(Boolean) : [];

      // New desired set
      const desired = Array.from(new Set([...selectedCoreBranches, ...valid]));
      // Merge with existing (avoid duplicates)
      const merged = Array.from(new Set([...existing, ...desired]));

      await checkApi.put(API.updateProfile, {
        section_type: 'branches',
        candidate_preferred_courses: merged.join(', ')
      });

      setSuccess('Saved preferred branches.');
      await loadPreferred();
      if (onUpdated) onUpdated();
      setTimeout(() => setSuccess(''), 2500);
    } catch (e) {
      setError('Failed to save preferred branches.');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    setError('');
    setSuccess('');
    // candidateId not needed when using my_profile/update_profile
    if (!canAddMore) { setError(`Maximum of ${MAX_PREFERRED} branches reached.`); return; }
    if (!selectedBranch?.value) { setError('Select a branch.'); return; }
    try {
      setSaving(true);
      
      // Get current preferred courses
      const currentResponse = await checkApi.get(API.profile);
      const currentCourses = currentResponse.data.candidate_preferred_courses || '';
      
      // Parse existing courses
      const existingCourses = currentCourses ? currentCourses.split(',').map(c => c.trim()).filter(Boolean) : [];
      
      // Find the branch name
      const branchName = allBranches.find(b => b.id === selectedBranch.value)?.branch_name || selectedBranch.label;
      
      // Add new branch if not already present
      if (!existingCourses.includes(branchName)) {
        const updatedCourses = [...existingCourses, branchName];
        
        // Update the courses field
        await checkApi.put(API.updateProfile, {
          section_type: 'branches',
          candidate_preferred_courses: updatedCourses.join(', ')
        });
        
        setSelectedBranch(null);
        setSuccess('Added preferred branch.');
        await loadPreferred();
        if (onUpdated) onUpdated();
        setTimeout(() => setSuccess(''), 2500);
      } else {
        setError('Branch already in preferred list.');
      }
    } catch (e) {
      setError('Failed to add preferred branch.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (branchName) => {
    if (!branchName) return;
    try {
      // Get current preferred courses
      const currentResponse = await checkApi.get(API.profile);
      const currentCourses = currentResponse.data.candidate_preferred_courses || '';
      
      // Parse existing courses
      const existingCourses = currentCourses ? currentCourses.split(',').map(c => c.trim()).filter(Boolean) : [];
      
      // Remove the branch from the list
      const updatedCourses = existingCourses.filter(course => course !== branchName);
      
      // Update the courses field
      await checkApi.put(API.updateProfile, {
        section_type: 'branches',
        candidate_preferred_courses: updatedCourses.join(', ')
      });
      
      await loadPreferred();
      if (onUpdated) onUpdated();
    } catch {
      setError('Failed to remove preferred branch.');
    }
  };

  const remaining = useMemo(() => Math.max(0, MAX_PREFERRED - preferredBranches.length), [preferredBranches.length]);

  return (
    <div className="bg-primary-100 rounded-lg p-4 shadow-lg shadow-primary-200">
      <div className="bg-tertiary-600 rounded-xl p-4 ">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 bg-pink-400 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-bold">4</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">My Preferred Branches</h3>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <span>Remaining:</span>
              <span className="font-semibold">{remaining}</span>
              <span>/ {MAX_PREFERRED}</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">{error}</div>
        )}
        {success && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">{success}</div>
        )}

        {/* List */}
        <div className="mt-4">
          {loading ? (
            <div className="text-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pink-500 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading preferred branches...</p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {preferredBranches.length === 0 && (
                <span className="px-3 py-1 rounded-full bg-gray-200 text-secondary-900">No preferred branches yet</span>
              )}
              {preferredBranches.map((item) => (
                <span key={item.id} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-gray-900">
                  <span className="truncate max-w-[220px]">{item.course_name}</span>
                  <button onClick={() => handleDelete(item.course_name)} className="text-red-600 hover:text-red-800" title="Remove">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Add - Checkbox group + Other input */}
        <div className="mt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Branches</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {CORE_BRANCHES.map((name) => {
                const checked = selectedCoreBranches.includes(name);
                return (
                  <label key={name} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${checked ? 'bg-green-50 border-green-400' : 'bg-white border-gray-200 hover:border-green-300'}`}>
                    <input
                      type="checkbox"
                      className="text-green-600 focus:ring-green-600"
                      checked={checked}
                      onChange={() => toggleCoreBranch(name)}
                    />
                    <span className="text-sm text-gray-800">{name}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Other (Please specify, comma separated)</label>
            <input
              type="text"
              value={otherBranchesInput}
              onChange={(e) => setOtherBranchesInput(e.target.value)}
              placeholder="e.g. CHEMICAL, METALLURGICAL"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            {otherValidationMsg && (
              <div className="mt-1 text-sm text-red-700">{otherValidationMsg}</div>
            )}
          </div>

          <div className="flex gap-3 pt-1">
            <button
              disabled={saving}
              onClick={handleSaveSelected}
              className="buttonClass flex items-center gap-2 px-4 py-2 rounded-lg disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <PlusIcon className="w-4 h-4" />
                  <span>Save Selection</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SDPrefBranches;